import { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { isRType as calcIsRType, isTraitType as calcIsTraitType } from '@aytm/app/stats/page/components/lib/utils'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { getModelValue } from '@aytm/app/stats/lib/stat-page'
import useOeBlock from '@aytm/app/stats/page/question-views/oe/states/useOeBlock'
import { CX_FAMILY_LIB } from '@aytm/app/stats/page/question-views/cx/lib/family'
import { CX_PROPS_BUILDERS } from '@aytm/app/stats/page/question-views/cx/lib/props'
import { calcRejects } from '@aytm/app/stats/page/question-views/cx/lib/props/word-cloud'
import { calcChartPlaceholderHeight } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/placeholder'
import useComboScope from '@aytm/app/stats/page/question-views/cx/states/useComboScope'

import Controls from '@aytm/app/stats/page/question-views/oe/components/Controls'

import ChartsView from '@aytm/app/stats/page/question-views/cx/components/views/ChartsView'
import WordCloudView from '@aytm/app/stats/page/question-views/cx/components/views/WordCloudView'

import { BLOCK_PROP_TYPES } from '@aytm/app/stats/page/question-views/oe/Block'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// Combo (cx / scx). Two surfaces rather than the family's four: the drill-down charts and the
// word cloud, both of which carry the scope list under them. There is no verbatim view -- combo
// has no quotes of its own -- and the controls strip is the open-ended one.
export const CX_CHART_VIEW_MODES = ['chart', 'sunburst', 'treemap']

export const BlockView = ({
  viewMode,
  components,
  chartHeight,
  controlsProps,
  chartProps,
  tableProps,
  collapsed = false,
  answer,
  wcProps
}) => {
  // combo carries no sentiments or tags, so unlike the rest of the open-ended family it draws
  // without waiting for the page-wide totals
  return (
    <>
      {isComponentOn(components, 'viewModes') && <Controls {...controlsProps} />}
      {!collapsed && (
        <>
          {CX_CHART_VIEW_MODES.includes(viewMode) && (
            <ChartsView
              viewMode={viewMode}
              components={components}
              answer={answer}
              chartHeight={chartHeight}
              chartProps={chartProps}
              tableProps={tableProps}
            />
          )}
          {'wordcloud' === viewMode && (
            <WordCloudView components={components} wcProps={wcProps} tableProps={tableProps} />
          )}
        </>
      )}
    </>
  )
}

const Block = props => {
  const { type, levels, answerType, trait, traitName } = props
  const { answer, answerVariants, handlers, onScopeChange } = props

  const isTraitType = calcIsTraitType(props)
  const rType = (calcIsRType(props) && getModelValue('rType')) || 'cases'

  // the drill-down rides in the same param bag as the rest, so it is split off before the block
  // state - which knows nothing about it - sees the bag
  const { qs, ...params } = props.params || {}

  const {
    scope,
    handlers: scopeHandlers,
    getScope
  } = useComboScope({
    param: qs,
    paramsRevision: props.paramsRevision,
    answer,
    answerVariants,
    onScopeChange
  })

  const extra = useMemo(
    () => ({ scope, levels, answerType, rType, trait, traitName, isTraitType }),
    [scope, levels, answerType, rType, trait, traitName, isTraitType]
  )

  const allHandlers = useMemo(() => ({ ...handlers, ...scopeHandlers }), [handlers, scopeHandlers])

  // The nested charts take a fixed height, which the mode they will be drawn in decides. Combo has
  // no area view, so the view mode never enters into it -- only what the chart is counting does.
  const chartHeight = useMemo(
    () =>
      calcChartPlaceholderHeight({
        chartMode: props.params?.cm,
        questionChartMode: props.questionChartMode,
        globalChartMode: getModelValue('chartMode'),
        answer,
        percentsOnly: true === props.format?.percentsOnly,
        isRType: calcIsRType(props),
        itemCount: answerVariants?.length || 0
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.params?.cm, props.questionChartMode, answer, props.format, props.type, props.variant, answerVariants]
  )

  const rejects = useMemo(() => calcRejects(type), [type])

  // the exporters read the level the block is showing off the payload the shell hands them
  const getExportExtra = useCallback(() => ({ scope: getScope() }), [getScope])

  const blockProps = useOeBlock(
    { ...props, params, handlers: allHandlers, lib: CX_FAMILY_LIB, rejects, getExportExtra, extra },
    CX_PROPS_BUILDERS
  )

  return (
    <BlockWrapper {...props}>
      <BlockView {...blockProps} chartHeight={chartHeight} />
    </BlockWrapper>
  )
}

Block.displayName = 'CxBlock'
BlockView.displayName = 'CxBlockView'

BlockView.propTypes = {
  viewMode: PropTypes.string.isRequired,
  components: PropTypes.object,
  chartHeight: PropTypes.number,
  answer: answerPropType,
  collapsed: PropTypes.bool,
  controlsProps: PropTypes.object,
  chartProps: PropTypes.object,
  tableProps: PropTypes.object,
  wcProps: PropTypes.object
}

Block.propTypes = {
  ...BLOCK_PROP_TYPES,
  levels: PropTypes.number,
  onScopeChange: PropTypes.func,
  paramsRevision: PropTypes.number,
  answerType: PropTypes.string,
  trait: PropTypes.object,
  traitName: PropTypes.string
}

export default Block
